/**
 * Quick Search (STEP 5, wired for the first time in Stage F5) — a
 * real Ctrl+K command palette: opens/closes via `store/layout.store.ts`,
 * captures the keyboard shortcut globally, debounced input state.
 * `useQuickSearchResults` was F0's own deliberate stub (always
 * returning `[]`, with a header comment inviting "a feature module" to
 * wire a real provider in later) — `features/dashboard/hooks/useGlobalSearch.ts`
 * is that real provider, combining permission-gated Quick Actions with
 * real `searchPatients` results. Domains with no backend search
 * endpoint (`DISABLED_SEARCH_DOMAINS`) are listed, not silently
 * omitted.
 */
import { useEffect, useRef, useState, type JSX } from 'react';
import { Dialog, TextField, Box, Typography, InputAdornment, List, ListItemButton, ListItemText, Chip, Stack, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useLayoutStore } from '@store/layout.store';
import { useGlobalSearch, DISABLED_SEARCH_DOMAINS, type GlobalSearchResult } from '@features/dashboard/hooks/useGlobalSearch';
import { useRecentlyViewedStore } from '@store/recently-viewed.store';
import { SwitchBranchDialog } from '@features/dashboard/components/SwitchBranchDialog';

function useQuickSearchResults(query: string): { results: GlobalSearchResult[]; loading: boolean } {
  return useGlobalSearch(query);
}

export function QuickSearch(): JSX.Element {
  const open = useLayoutStore((state) => state.quickSearchOpen);
  const setOpen = useLayoutStore((state) => state.setQuickSearchOpen);
  const [query, setQuery] = useState('');
  const { results, loading } = useQuickSearchResults(query);
  const navigate = useNavigate();
  const recordView = useRecentlyViewedStore((state) => state.recordView);
  const inputRef = useRef<HTMLInputElement>(null);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setOpen(true);
      }
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setOpen]);

  function handleSelect(result: GlobalSearchResult): void {
    if (result.kind === 'patient' && result.patient) {
      recordView({ id: result.patient.id, displayName: `${result.patient.givenName} ${result.patient.familyName ?? ''}`.trim(), uhid: result.patient.uhid });
    }
    setOpen(false);
    setQuery('');
    if (result.path) {
      navigate(result.path);
    } else if (result.kind === 'action' && result.action?.id === 'switch-branch') {
      setBranchDialogOpen(true);
    }
  }

  // Only a result with neither a real route NOR a special in-dialog action (currently
  // just "Switch Branch") is genuinely inert — everything else must stay clickable.
  function isSelectable(result: GlobalSearchResult): boolean {
    return Boolean(result.path) || (result.kind === 'action' && result.action?.id === 'switch-branch');
  }

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { position: 'fixed', top: 96 } } }}>
        <Box p={1.5}>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Search patients, or type a quick action…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: loading ? <CircularProgress size={16} /> : undefined,
              },
            }}
          />
          <Box mt={1} minHeight={60}>
            {results.length === 0 ? (
              <Box display="flex" alignItems="center" justifyContent="center" minHeight={60}>
                <Typography variant="body2" color="text.secondary">
                  {query.trim().length >= 2 ? 'No matches.' : 'Type a patient name, or a quick action like “bill” or “book”.'}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {results.map((result) => (
                  <ListItemButton key={result.id} onClick={() => handleSelect(result)} disabled={!isSelectable(result)}>
                    <ListItemText
                      primary={result.label}
                      secondary={result.kind === 'action' ? 'Quick action' : result.secondary}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
            {query.trim().length >= 2 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} mt={1.5}>
                <Typography variant="caption" color="text.secondary" mr={0.5}>
                  Not searchable yet:
                </Typography>
                {DISABLED_SEARCH_DOMAINS.map((domain) => (
                  <Chip key={domain.label} size="small" variant="outlined" label={domain.label} title={domain.reason} />
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </Dialog>
      <SwitchBranchDialog open={branchDialogOpen} onClose={() => setBranchDialogOpen(false)} />
    </>
  );
}
